import {
  Box,
  Text,
  Dialog,
  Button,
  Stack,
  Badge,
  Notice,
  Alert,
} from 'braid-design-system';
import queryString from 'query-string';
import React, { useReducer } from 'react';
import { useNavigate } from 'react-router-dom';

import { CustomRadioButton } from 'src/components/CustomRadioButton/CustomRadioButton';
import Routes from 'src/config/routes';
import type {
  ClientListingForListingsTab,
  ListingProductForListingsTab,
} from 'src/types/graphql-derived';
import { getBoostBundlePrices } from 'src/utils/feature-flags';
import {
  decimalFormatPriceBand,
  formatPriceBand,
} from 'src/utils/investmentBands';

import * as styles from './BoostDialogBundleDiscount.css';

const SEARCH_BOOST_PRODUCT = 'website-search-boost' as const;
const EMAIL_BOOST_PRODUCT = 'email-boost' as const;
const BUNDLE_BOOST_PRODUCT = 'website-search-boost-and-email-boost' as const;

interface BoostDialogBundleDiscountProps {
  isOpen: boolean;
  listing: ClientListingForListingsTab;
  isLus: boolean;
  searchBoostProduct: ListingProductForListingsTab;
  emailBoostProduct: ListingProductForListingsTab;
  onClose: () => void;
}

export const BoostDialogBundleDiscount = (
  props: BoostDialogBundleDiscountProps,
) => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);

  const handleClose = () => {
    dispatch({ type: 'close-dialog' });
    props.onClose();
  };

  const selectedProductTokens = () => {
    switch (state.selectedProduct) {
      case 'none':
        return [];
      case SEARCH_BOOST_PRODUCT:
        return [props.searchBoostProduct.productToken];
      case EMAIL_BOOST_PRODUCT:
        return [props.emailBoostProduct.productToken];
      case BUNDLE_BOOST_PRODUCT:
        return [
          props.emailBoostProduct.productToken,
          props.searchBoostProduct.productToken,
        ];
    }
  };

  const bundlePrice = getBoostBundlePrices({ isLus: props.isLus });

  return (
    <Dialog
      id="select-boost-packages"
      title="Select boost packages"
      width="small"
      open={props.isOpen}
      onClose={handleClose}
    >
      <Stack space="medium">
        <Alert tone="promote">
          <Text>
            Until 31 March 2025, maximise your ad exposure and save{' '}
            {formatPriceBand(bundlePrice.savings / 100)}.
          </Text>
        </Alert>

        <Text tone="secondary">
          Ready to accelerate your sale? Choose how to boost your ad&apos;s
          visibility to buyers.
        </Text>
      </Stack>

      <Stack space="large">
        <Stack space="medium">
          <Box className={styles.radioButtonLineItem}>
            <CustomRadioButton
              selected={state.selectedProduct === SEARCH_BOOST_PRODUCT}
              onChange={() =>
                dispatch({
                  type: 'select-product',
                  product: SEARCH_BOOST_PRODUCT,
                })
              }
              label={<Text>Website Search Boost</Text>}
              description={
                <Text tone="secondary">
                  Stand out where it matters – at the top of search results and
                  on our homepage for buyers in your location.
                </Text>
              }
            />

            <Text
              weight={
                state.selectedProduct === SEARCH_BOOST_PRODUCT
                  ? 'strong'
                  : 'regular'
              }
              data={{ testid: 'boost-dialog-search-boost-price' }}
            >
              {decimalFormatPriceBand(props.searchBoostProduct.cost / 100)}
            </Text>
          </Box>

          <Box className={styles.radioButtonLineItem}>
            <CustomRadioButton
              selected={state.selectedProduct === EMAIL_BOOST_PRODUCT}
              onChange={() =>
                dispatch({
                  type: 'select-product',
                  product: EMAIL_BOOST_PRODUCT,
                })
              }
              label={<Text>Email Boost</Text>}
              description={
                <Text tone="secondary">
                  Reach motivated buyers directly – get featured at the top of
                  our daily recommendation emails.
                </Text>
              }
            />

            <Text
              weight={
                state.selectedProduct === EMAIL_BOOST_PRODUCT
                  ? 'strong'
                  : 'regular'
              }
              data={{ testid: 'boost-dialog-email-boost-price' }}
            >
              {decimalFormatPriceBand(props.emailBoostProduct.cost / 100)}
            </Text>
          </Box>

          <Box className={styles.radioButtonLineItem}>
            <CustomRadioButton
              selected={state.selectedProduct === BUNDLE_BOOST_PRODUCT}
              onChange={() =>
                dispatch({
                  type: 'select-product',
                  product: BUNDLE_BOOST_PRODUCT,
                })
              }
              label={<Text>Website Search Boost & Email Boost</Text>}
              description={
                <Stack space="small">
                  <Badge tone="promote">Limited time offer</Badge>

                  <Text tone="secondary">
                    More visibility, better results. Get both and save until 31
                    March 2025.
                  </Text>
                </Stack>
              }
            />

            <Stack space="small">
              <Text
                weight={
                  state.selectedProduct === BUNDLE_BOOST_PRODUCT
                    ? 'strong'
                    : 'regular'
                }
                data={{ testid: 'boost-dialog-bundle-original-price' }}
              >
                <Box
                  component="span"
                  style={{ textDecoration: 'line-through' }}
                >
                  {decimalFormatPriceBand(bundlePrice.original / 100)}
                </Box>
              </Text>
              <Text
                weight={
                  state.selectedProduct === BUNDLE_BOOST_PRODUCT
                    ? 'strong'
                    : 'regular'
                }
                data={{ testid: 'boost-dialog-bundle-discount-price' }}
              >
                {decimalFormatPriceBand(bundlePrice.discount / 100)}
              </Text>
            </Stack>
          </Box>
        </Stack>

        {state.hasSubmissionError && (
          <Box paddingY="small">
            <Notice
              tone="critical"
              data={{ testid: 'boost-dialog-error-feedback' }}
            >
              <Text>Please select a product to proceed.</Text>
            </Notice>
          </Box>
        )}

        <Box>
          <Button
            onClick={() => {
              const productTokens = selectedProductTokens();

              if (productTokens.length === 0) {
                dispatch({ type: 'invalid-submission' });
                return;
              }

              navigate(
                queryString.stringifyUrl({
                  url: Routes.PAYMENT,
                  query: {
                    productToken: productTokens,
                    listingId: props.listing.id,
                  },
                }),
              );
            }}
          >
            Continue to payment
          </Button>
        </Box>
      </Stack>
    </Dialog>
  );
};

interface State {
  hasSubmissionError: boolean;
  selectedProduct:
    | 'none'
    | typeof SEARCH_BOOST_PRODUCT
    | typeof EMAIL_BOOST_PRODUCT
    | typeof BUNDLE_BOOST_PRODUCT;
}

type Action =
  | { type: 'select-product'; product: State['selectedProduct'] }
  | { type: 'invalid-submission' }
  | { type: 'close-dialog' };

const initialState: State = {
  hasSubmissionError: false,
  selectedProduct: 'none',
};

const reducer = (state: State, action: Action): typeof state => {
  switch (action.type) {
    case 'select-product':
      return {
        ...state,
        hasSubmissionError: false,
        selectedProduct: action.product,
      };
    case 'invalid-submission':
      return {
        ...state,
        hasSubmissionError: true,
      };
    case 'close-dialog':
      return { ...initialState };
  }
};
